import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Alert, AlertDescription } from '../ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  ArrowLeft,
  Trash2,
  Edit,
  Plus,
  SkipForward
} from 'lucide-react';

const ACTION_CONFIG = {
  create: { label: 'Crea', color: 'bg-emerald-100 text-emerald-700', icon: Plus },
  update: { label: 'Aggiorna', color: 'bg-blue-100 text-blue-700', icon: Edit },
  dismiss: { label: 'Dismetti', color: 'bg-orange-100 text-orange-700', icon: Trash2 },
  skip: { label: 'Salta', color: 'bg-gray-100 text-gray-600', icon: SkipForward },
  error: { label: 'Errore', color: 'bg-red-100 text-red-700', icon: XCircle }
};

const ActionBadge = ({ action }) => {
  const config = ACTION_CONFIG[action] || ACTION_CONFIG.error;
  const Icon = config.icon;
  return (
    <Badge variant="outline" className={`${config.color} flex items-center gap-1`}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
};

export function InventoryPreviewStep({ validation, loading, onCancel, onConfirm }) {
  if (!validation) return null;

  return (
    <div className="space-y-4">
      {/* Riepilogo */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="pt-3 pb-3">
            <div className="text-center">
              <p className="text-2xl font-bold text-emerald-700">{validation.create_items}</p>
              <p className="text-xs text-emerald-600">Da creare</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-3 pb-3">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-700">{validation.update_items}</p>
              <p className="text-xs text-blue-600">Da aggiornare</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="pt-3 pb-3">
            <div className="text-center">
              <p className="text-2xl font-bold text-orange-700">{validation.dismiss_items}</p>
              <p className="text-xs text-orange-600">Da dismettere</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gray-50 border-gray-200">
          <CardContent className="pt-3 pb-3">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-700">{validation.skip_items}</p>
              <p className="text-xs text-gray-600">Saltati</p>
            </div>
          </CardContent>
        </Card>
        <Card className={`${validation.error_items > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50'}`}>
          <CardContent className="pt-3 pb-3">
            <div className="text-center">
              <p className={`text-2xl font-bold ${validation.error_items > 0 ? 'text-red-700' : 'text-gray-400'}`}>
                {validation.error_items}
              </p>
              <p className={`text-xs ${validation.error_items > 0 ? 'text-red-600' : 'text-gray-500'}`}>
                Errori
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Errori */}
      {validation.errors.length > 0 && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>{validation.errors.length} errori trovati</strong> - Le righe con errori non verranno importate.
            <ScrollArea className="h-24 mt-2">
              <ul className="text-sm space-y-1">
                {validation.errors.slice(0, 10).map((err) => (
                  <li key={`err-${err.row}-${err.serial_number || err.message.substring(0, 20)}`}>
                    Riga {err.row}: {err.message}
                  </li>
                ))}
                {validation.errors.length > 10 && (
                  <li className="text-muted-foreground">
                    ... e altri {validation.errors.length - 10} errori
                  </li>
                )}
              </ul>
            </ScrollArea>
          </AlertDescription>
        </Alert>
      )}

      {/* Avvisi */}
      {validation.warnings.length > 0 && (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            <strong>{validation.warnings.length} avvisi</strong> - Le righe verranno importate ma controlla i dati.
            <ScrollArea className="h-20 mt-2">
              <ul className="text-sm space-y-1">
                {validation.warnings.slice(0, 5).map((warn) => (
                  <li key={`warn-${warn.row}-${warn.serial_number || warn.message.substring(0, 20)}`}>
                    Riga {warn.row}: {warn.message}
                  </li>
                ))}
              </ul>
            </ScrollArea>
          </AlertDescription>
        </Alert>
      )}

      {/* Tabella anteprima */}
      <div>
        <h4 className="font-medium mb-2">Anteprima ({validation.preview_data.length} righe)</h4>
        <ScrollArea className="h-64 border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">#</TableHead>
                <TableHead>Azione</TableHead>
                <TableHead>Seriale</TableHead>
                <TableHead>Articolo</TableHead>
                <TableHead>Magazzino</TableHead>
                <TableHead>Stato</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {validation.preview_data.slice(0, 50).map((row) => (
                <TableRow key={`preview-${row.row_number}-${row.serial_number}`} className={row.action === 'error' ? 'bg-red-50' : ''}>
                  <TableCell className="text-muted-foreground">{row.row_number}</TableCell>
                  <TableCell>
                    <ActionBadge action={row.action} />
                  </TableCell>
                  <TableCell className="font-mono text-sm">{row.serial_number}</TableCell>
                  <TableCell>
                    {row.catalog_brand && row.catalog_model ? (
                      <span>{row.catalog_brand} {row.catalog_model}</span>
                    ) : (
                      <span className="text-muted-foreground">{row.catalog_item_code}</span>
                    )}
                  </TableCell>
                  <TableCell>{row.warehouse_code}</TableCell>
                  <TableCell>
                    {row.errors.length > 0 ? (
                      <span className="text-red-600 text-sm">{row.errors[0]}</span>
                    ) : (
                      <span className="text-sm">{row.stato}</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>

      {/* Azioni */}
      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={onCancel}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Annulla
        </Button>
        <Button
          onClick={onConfirm}
          disabled={loading || !validation.is_valid}
          className={validation.is_valid ? '' : 'opacity-50'}
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <CheckCircle2 className="h-4 w-4 mr-2" />
          )}
          {validation.is_valid ? 'Conferma Import' : 'Correggi Errori'}
        </Button>
      </div>
    </div>
  );
}
